"use client";
import type { Job } from "@/lib/api/types";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenuItem,
  ContextMenuSeparator,
} from "@/components/ui/context-menu";
import type { ActionMenuKit } from "@/components/ui/action-menu-kit";
import { Icon } from "@/components/ui/icon";

export interface JobActionsHandlers {
  onView?: () => void;
  onClone?: () => void;
  onSearchTalent?: () => void;
  onClose: () => void;
  onReopen: () => void;
  onArchive: () => void;
  onImport?: () => void;
  cloneDisabled?: boolean;
  searchTalentDisabled?: boolean;
}

interface JobActionsMenuProps extends JobActionsHandlers {
  job: Job;
  align?: "start" | "end";
  trigger?: React.ReactNode;
}

export function renderJobActionsMenuItems(
  job: Job,
  handlers: JobActionsHandlers,
  { Item, Separator }: ActionMenuKit,
) {
  const {
    onView,
    onClone,
    onSearchTalent,
    onClose,
    onReopen,
    onArchive,
    onImport,
    cloneDisabled,
    searchTalentDisabled,
  } = handlers;

  return (
    <>
      {onImport && (
        <>
          <Item onSelect={onImport}>
            <Icon
              name="IconPeopleAdd"
              size={14}
              className="text-heading opacity-50"
            />
            Import profiles
          </Item>
          <Separator />
        </>
      )}
      {job.status !== "draft" && (
        <>
          {onView && (
            <>
              <Item onSelect={onView}>
                <Icon
                  name="IconSquareArrowTopRight2"
                  size={14}
                  className="text-heading opacity-50"
                />
                View job
              </Item>
              <Separator />
            </>
          )}
          {onClone && (
            <Item
              onSelect={onClone}
              disabled={cloneDisabled}
              data-testid={`job-action-clone-${job.id}`}
            >
              <Icon
                name="IconFiles"
                size={14}
                className="text-heading opacity-50"
              />
              Clone job
            </Item>
          )}
          {onSearchTalent && (
            <Item
              onSelect={onSearchTalent}
              disabled={searchTalentDisabled}
              data-testid={`job-action-search-talent-${job.id}`}
            >
              <Icon
                name="IconSearchIntelligence"
                size={14}
                className="text-heading opacity-50"
              />
              Search talent
            </Item>
          )}
          <Separator />
          {job.status === "active" ? (
            <Item onSelect={onClose}>
              <Icon
                name="IconCrossLarge"
                size={14}
                className="text-heading opacity-50"
              />
              Close job
            </Item>
          ) : (
            <Item onSelect={onReopen}>
              <Icon
                name="IconCrossLarge"
                size={14}
                className="text-heading opacity-50"
              />
              Reopen job
            </Item>
          )}
        </>
      )}
      <Item
        onSelect={onArchive}
        className="text-red-400 focus:bg-destructive/10 focus:text-red-300"
      >
        <Icon name="IconTrashCanSimple" size={14} />
        Archive job
      </Item>
    </>
  );
}

export const jobActionsDropdownKit: ActionMenuKit = {
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator,
};

export const jobActionsContextKit: ActionMenuKit = {
  Item: ContextMenuItem,
  Separator: ContextMenuSeparator,
};

export function JobActionsMenu({
  job,
  onView,
  onClone,
  onSearchTalent,
  onClose,
  onReopen,
  onArchive,
  cloneDisabled = false,
  searchTalentDisabled = false,
  onImport,
  align = "end",
  trigger,
}: JobActionsMenuProps) {
  const handlers = {
    onView,
    onClone,
    onSearchTalent,
    onClose,
    onReopen,
    onArchive,
    onImport,
    cloneDisabled,
    searchTalentDisabled,
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center cursor-pointer"
          >
            <div className="opacity-50 hover:opacity-80 transition-opacity">
              <Icon
                name="IconDotGrid1x3Vertical"
                size={16}
                className="text-heading"
              />
            </div>
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        sideOffset={8}
        className="w-48"
        onClick={(e) => e.stopPropagation()}
      >
        {renderJobActionsMenuItems(job, handlers, jobActionsDropdownKit)}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function JobActionsContextMenuContent({
  job,
  ...handlers
}: JobActionsHandlers & { job: Job }) {
  return renderJobActionsMenuItems(job, handlers, jobActionsContextKit);
}
